import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Slider from 'react-slick';
import { Badge, Icon, List } from '@pella/sash-framework';
import { Image } from '../../../';
import { isValidArray } from '../../../utilities/validations';

import Render3DImage from '../3d-image';

import styles from './index.module.scss';

const PrevArrow = (props) => {
	const { className, iconClass, style, onClick } = props;
	return (
		<button
			className={className}
			style={{ ...style }}
			onClick={onClick}
			aria-label='Previous'
		>
			<Icon
				name={props.icon}
				className={iconClass}
			/>
		</button>
	);
};

PrevArrow.propTypes = {
	className: PropTypes.string,
	iconClass: PropTypes.string,
	style: PropTypes.object,
	onClick: PropTypes.func,
	icon: PropTypes.string,
};

const NextArrow = (props) => {
	const { className, iconClass, style, onClick } = props;
	return (
		<button
			className={className}
			style={{ ...style }}
			onClick={onClick}
			aria-label='Next'
		>
			<Icon
				name={props.icon}
				className={iconClass}
			/>
		</button>
	);
};

NextArrow.propTypes = {
	className: PropTypes.string,
	iconClass: PropTypes.string,
	style: PropTypes.object,
	onClick: PropTypes.func,
	icon: PropTypes.string,
};

const ImageSlider = ({ data, className, productType, ...other }) => {
	const sliderRef = useRef(null);
	const thumbRef = useRef(null);
	const [sliderNav, setSliderNav] = useState({ nav1: null, nav2: null });

	useEffect(() => setSliderNav({ nav1: sliderRef.current, nav2: thumbRef.current }), []);

	if (!data) return null;

	const renderImageBadges = (badges, truncate = false) => (
		<List
			bullets='none'
			layout='inline'
			className={`absolute z-2 space-x-1 ${truncate ? 'left-1 top-1' : 'left-2 top-2'}`}
		>
			{badges.map((badge, index) => {
				if (!badge.color || !badge.badge) return null;

				return (
					<li key={index}>
						<Badge
							color={badge.color}
							dangerouslySetInnerHTML={{ __html: badge.badge.substring(0, truncate ? 20 : 100) }}
							className='!border-0 !px-1 !pb-1'
							size='md'
						/>
					</li>
				);
			})}
		</List>
	);

	return (
		<div
			className={`${styles.imageSlider} relative sm:mb-8 md:sticky xl:mb-0 ${className || ''}`}
			{...other}
		>
			<div className={styles.imageSlider_sticky}>
				<noscript>
					<Image
						data={data.cob?.[0] || data.beauty_images?.[0]}
						className={styles.imageSlider_COBImage}
						loading='eager'
					/>
				</noscript>

				<Slider
					arrows={false}
					dots={false}
					asNavFor={sliderNav.nav2}
					ref={(slider) => (sliderRef.current = slider)}
					infinite={false}
					className={styles.imageSlider_MainSlider}
					nextArrow={
						<NextArrow
							iconClass='block text-5xl'
							icon='arrow_right'
						/>
					}
					prevArrow={
						<PrevArrow
							iconClass='block text-5xl'
							icon='arrow_left'
						/>
					}
					centerMode={false}
					lazyLoad={true}
					swipeToSlide={false}
					draggable={false}
					touchMove={false}
					responsive={[
						{
							breakpoint: 767,
							settings: { arrows: true },
						},
					]}
				>
					{isValidArray(data.cob) &&
						data.cob.map((cob, index) => (
							<div
								key={index}
								className='relative'
							>
								{isValidArray(cob.badge) && renderImageBadges(cob.badge)}

								<Image
									data={cob}
									key={index}
									className={`${styles.imageSlider_COBImage} !block bg-white`}
									objectFit='contain'
									loading='eager'
								/>
							</div>
						))}

					{data.aug_3d?.url && <Render3DImage data={data.aug_3d.url} />}

					{isValidArray(data.beauty_images) &&
						data.beauty_images.map((image, index) => (
							<div
								key={index}
								className='relative'
							>
								{isValidArray(image.badge) && renderImageBadges(image.badge)}

								<Image
									data={image}
									key={index}
									loading={!data.cob?.[0] && index === 0 ? 'eager' : 'lazy'}
									className='!block'
								/>
							</div>
						))}
				</Slider>

				<Slider
					arrows={true}
					dots={false}
					asNavFor={sliderNav.nav1}
					ref={(slider) => (thumbRef.current = slider)}
					slidesToShow={5}
					slidesToScroll={1}
					className={`${styles.imageSlider_ThumbSlider} !flex md:mt-5 md:h-full md:items-center md:px-6 lg:px-0`}
					infinite={false}
					lazyLoad={true}
					centerMode={false}
					focusOnSelect={true}
					responsive={[
						{
							breakpoint: 1280,
							settings: { slidesToShow: 3 },
						},
						{
							breakpoint: 1023,
							settings: { slidesToShow: 5 },
						},
						{
							breakpoint: 550,
							settings: { slidesToShow: 3 },
						},
					]}
					nextArrow={
						<NextArrow
							iconClass='block text-2xl lg:text-4xl'
							icon='keyboard_arrow_right'
						/>
					}
					prevArrow={
						<PrevArrow
							iconClass='block text-2xl lg:text-4xl'
							icon='keyboard_arrow_left'
						/>
					}
				>
					{isValidArray(data.cob) &&
						data.cob.map((cob, index) => (
							<div
								className='relative'
								key={index}
							>
								{isValidArray(cob.badge) && renderImageBadges(cob.badge, true)}

								<Image
									data={cob}
									key={index}
									className='!block'
									objectFit='contain'
								/>
							</div>
						))}

					{data.aug_3d?.url && (
						<div className={styles.imageSlider_Thumb}>
							<Icon name='3d_rotation' />
							{data.cob?.[0] && (
								<div className='relative'>
									{isValidArray(data.cob[0].badge) && renderImageBadges(data.cob[0].badge, true)}

									<Image
										data={data.cob[0]}
										className='!block'
										objectFit='contain'
									/>
								</div>
							)}
						</div>
					)}

					{isValidArray(data.beauty_images) &&
						data.beauty_images.map((image, index) => (
							<div
								key={index}
								className='relative'
							>
								{isValidArray(image.badge) && renderImageBadges(image.badge, true)}

								<Image
									key={index}
									data={image}
									className='!block'
								/>
							</div>
						))}
				</Slider>
			</div>
		</div>
	);
};

ImageSlider.propTypes = {
	data: PropTypes.object,
	className: PropTypes.string,
	productType: PropTypes.string,
};

export default ImageSlider;
